/**
 * Downloads a file with the specified content and filename.
 * Creates a temporary blob URL and initiates the download through a dynamically created link element.
 *
 * @param {string} content - The content to be downloaded as a file
 * @param {string} filename - The name of the file to be downloaded
 * @param {string} contentType - The MIME type of the content (e.g., 'text/plain', 'application/json')
 * @returns {void}
 *
 * @example
 * download('Hello, World!', 'hello.txt', 'text/plain');
 * download(JSON.stringify(data), 'data.json', 'application/json');
 */
export const download = (
    content: string,
    filename: string,
    contentType: string
) => {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.click();
};

/**
 * Downloads a file from a URL and saves it with a specified filename.
 *
 * @param {string} url - The URL of the file to download
 * @param {string} filename - The name of the file to save
 * @returns {void}
 *
 * @example
 * downloadFromUrl('https://example.com/file.txt', 'file.txt');
 */
export const downloadFromUrl = async (url: string, filename: string) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
};

/**
 * Formats a file size in bytes into a human-readable string.
 *
 * @param {number} bytes - The file size in bytes.
 * @returns {string} The formatted file size as a string with appropriate units.
 */
export const formatFileSize = (bytes: number) => {
    const BYTE_UNIT = 1024;
    const EMPTY_SIZE = 0;
    const EMPTY_BYTE_LABEL = '0 Byte';
    const DECIMAL_PLACES = 2;

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === EMPTY_SIZE) return EMPTY_BYTE_LABEL;
    const index = Math.floor(Math.log(bytes) / Math.log(BYTE_UNIT));
    return `${(bytes / BYTE_UNIT ** index).toFixed(DECIMAL_PLACES)} ${sizes[index]}`;
};

const sanitize = (input: string): string =>
    input
        .toLowerCase()
        .trim()
        .replaceAll(/[^\w\-.]/g, '_')
        .replaceAll(/_+/g, '_')
        .replace(/^_/, '')
        .replace(/_$/, '');

export const getValidFilename = ([idn, organization, datetime, gmt]: [
    string | null | undefined,
    string | null | undefined,
    string | null | undefined,
    string | null | undefined
]): string => {
    const cleanOrg = organization ? sanitize(organization) : 'unknown';
    const cleanDate = datetime ? sanitize(datetime.replaceAll(':', '-')) : '';
    const cleanIdn = idn ? sanitize(idn) : '';
    const cleanGmt = gmt ? sanitize(gmt) : '';

    return `${cleanIdn}_${cleanOrg}_${cleanDate}_${cleanGmt}`;
};
